import { ServerConfig } from "@/config/config";
import { Axios } from "@/utils/axios";
import { EventEmitter } from "@/utils/event-emitter";
import { AuthService } from ".";
import { Tier } from "@/utils/payments/tiers";
import { ElectronFunctions } from "@/utils/electron/electron";
import { HttpError } from "@/utils/services/http-error";

export interface ILockscreenNfcBadge {
	ID:number;
	Label:string;
	HasPinCode:boolean;
	Uid?:string;
}

export interface IValidateNfcBadgeResponse{
	Valid:boolean;
	Badge?:ILockscreenNfcBadge;
}

class LockScreenServiceClass extends EventEmitter {
	private readonly nfcBadgesUrl:string = `${ServerConfig.auth}/auth/lockscreen/nfc-badges`
	public readonly MAX_BADGES = 10;
	private broadcastChannel:BroadcastChannel = new BroadcastChannel("lockscreen")
	private _lastUsedNfcTag = "";
	public get currentNfcTag():string{
		return this._lastUsedNfcTag;
	}
	public set currentNfcTag(value:string){
		sessionStorage.setItem("current-in-use-nfc-tag", value);
		this._lastUsedNfcTag = value;
	}

	public _isLocked = localStorage.getItem("is-locked") == "true";
	public get isLocked():boolean{
		if (!this.isFeatureEnabled) return false;
		return this._isLocked;
	}
	public set isLocked(value:boolean){
		if (!this.isFeatureEnabled) {
			value = false;
		}
		localStorage.setItem("is-locked", `${value}`);
		this._isLocked = value;
		setTimeout(()=>{
			this.broadcastChannel.postMessage("is-locked-status-changed");
		}, 100);
	}

	public isFeatureEnabled():boolean {
		return AuthService.wfCompany.Tier == Tier.ENTERPRISE;
	}

	public isNfcUnlockFeatureEnabled():boolean{
		return this.isFeatureEnabled();
	}

	public constructor(){
		super();
		this.broadcastChannel.addEventListener("message", (message:MessageEvent)=>{
			if (message.data == "is-locked-status-changed") {
				this._isLocked = localStorage.getItem("is-locked") == "true";
			}
		});
	}

	public async getNfcBadges():Promise<ILockscreenNfcBadge[]>{
		let result = await Axios.get(`${this.nfcBadgesUrl}`);
		return result.data;
	}

	public async addNfcBadge(label:string, uid:string, pinCode:string):Promise<ILockscreenNfcBadge>{
		let result = await Axios.post(`${this.nfcBadgesUrl}`, {
			Label: label,
			Uid: uid,
			PinCode: pinCode
		});
		return result.data;
	}

	public async deleteNfcBadge(id:number):Promise<void>{
		await Axios.delete(`${this.nfcBadgesUrl}/${id}`);
	}

	public async fetchBadgeByUid(uid:string):Promise<ILockscreenNfcBadge>{
		let result = await Axios.get(`${this.nfcBadgesUrl}/by-uid/${encodeURIComponent(uid)}`);
		return result.data;
	}

	public async badgeExists(uid:string):Promise<boolean>{
		try{
			await this.fetchBadgeByUid(uid);
			return true;
		}catch(err){
			let hErr = err as HttpError;
			if (hErr.key == "ELEMENT_NOT_FOUND"){
				hErr.dontShow = true;
				return false;
			}
			throw err;
		}
	}

	public async validateNfcBadge(uid:string, pinCode:string):Promise<IValidateNfcBadgeResponse>{
		let result = await Axios.post(`${this.nfcBadgesUrl}/validate`, {Uid: uid, PinCode: pinCode});
		return result.data;
	}

	public async onLogin(){
		let settings = ElectronFunctions.getNfcSettings();
		this._lastUsedNfcTag = sessionStorage.getItem("current-in-use-nfc-tag") || "";
		if (settings.requireTagOnStart && !this.currentNfcTag) {
			this.isLocked = true;
		}
	}

	public isPinCodeValid(code:string):boolean{
		if (!code) {
			return true;
		}
		return /^\d{4,8}$/.test(code);
	}

	public async editBadge(id:number, label:string, pinCode:string | null = null):Promise<ILockscreenNfcBadge>{
		let result = await Axios.put(`${this.nfcBadgesUrl}/${id}`, {Label: label, PinCode: pinCode});
		return result.data;
	}
}

export const LockScreenService = new LockScreenServiceClass();
